// @ts-check // Enable script type checking for this file
import {gmAPI, MapService} from '@/services/MapService';
import {Graphic} from '../Graphic';
import {SubscriptionManager} from '@/lib/filters/subscriptions';
import {SVGOverlayView} from '../SVGOverlayView';
import * as colors from '@/lib/colors';

/**
 * @typedef {Object} PolylineData
 * @property {google.maps.LatLngLiteral[]} path
 * @property {string} strokeColor
 *
 * @typedef {google.maps.PolylineOptions} PolylineOptions
 * @typedef {import('../GraphicService').GraphicData & PolylineData} PolylineGraphicData
 * @typedef {google.maps.LatLngLiteral} LatLngLiteral
 * @typedef {import('@/lib/filters/subscriptions').SubscriptionEvent} SubscriptionEvent
 * @typedef {import('@/lib/filters/subscriptions').SubscriptionResult} SubscriptionResult
 * @typedef {import('@/services/GraphicService').ToolbarOptions} ToolbarOptions
 * @typedef {import('@/services/GraphicService/Graphic').EditingContext} EditingContext
 *
 */

/**
 * Gets the default renderer options for use in the AccessRouteGraphic.
 * @private
 * @return {google.maps.DirectionsRendererOptions}
 */
function getDefaultOptions() {
  return {
    draggable: true,
    preserveViewport: true,
    polylineOptions: {
      strokeColor: colors.blue,
      strokeWeight: 4,
      draggable: false,
      editable: false,
    },
  };
}

/**
 * Given a google maps polyline, converts to lat/lng literals array
 * @param {google.maps.Polyline} polyline
 * @return {google.maps.LatLngLiteral[]}
 */
function getPathFromPolyline(polyline) {
  return polyline?.getPath()?.getArray().map((latLng) => ({
    lat: latLng.lat(),
    lng: latLng.lng(),
  }));
}

/**
 * @param {google.maps.LatLngLiteral[]} path
 * @return {google.maps.LatLngBounds}
 */
function getBoundsFromPath(path) {
  const bounds = new gmAPI.LatLngBounds();
  path.forEach((point) => {
    bounds.extend(point);
  });
  return bounds;
}

/**
 * Gets the total distance of a polyline
 * @param {google.maps.LatLngLiteral[]} path
 * @return {number}
 */
function calculateTotalDistance(path) {
  const distanceInMeters = gmAPI.geometry.spherical.computeLength(path);
  // converts distance in meters to distance in miles
  return distanceInMeters * 0.000621371;
}

/**
 * @classdesc Class representing a polygon graphic.
 * @class
 * @augments Graphic
 */
class PolylineGraphic extends Graphic {
  /**
   * Creates a new PolylineGraphic object. Can be initialized from a PolylineOptions object.
   * @param {PolylineGraphicData} polylineData The options for the polyline.
   */
  constructor(polylineData) {
    super();
    /** @type {google.maps.Polyline} the google maps polyline representation */
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- JSDoc type declaration
    this.polyline;
    /** @type {google.maps.LatLngLiteral[]} array of lat/lngs for line path */
    this.path = polylineData.path;
    /** @type {number} the total distance of the polyline */
    this.totalDistance = calculateTotalDistance(this.path);

    /** @type {string} */
    // Default to standard orange if no color is available
    this.color = polylineData.strokeColor ?? '#e18d3d';

    // Create SVG and display it first
    const defaultOptions = getDefaultOptions();
    const options = {...defaultOptions.polylineOptions, ...polylineData};
    this.svgOverlay = new SVGOverlayView(options, this.uuid, 'polyline');
    this.svgOverlay.setMap(MapService.getMap());

    /** @type {boolean} */
    this.editing = false;
    /** @type {boolean} */
    this._clickable = false;
    /** @type {SubscriptionManager} */
    this.subscriptions = new SubscriptionManager();
  }

  /**
   * Adds the Polyline representation to the map using a 2D array of lat/lng objects.
   */
  displayPolyline() {
    // Hide the svg overlay version of the polyline
    if (this.svgOverlay) {
      this.svgOverlay.setMap(null);
    }
    // Create, add listeners to, and display a google maps polyline
    this.polyline = new gmAPI.Polyline({
      clickable: true,
      editable: true,
      path: this.path,
      strokeColor: this.color,
      strokeOpacity: 1.0,
      strokeWeight: 4,
    });
    ['insert_at', 'set_at', 'remove_at'].forEach((eventType) => {
      this.polyline.getPath().addListener(eventType, () => {
        this.path = getPathFromPolyline(this.polyline);
        this.totalDistance = calculateTotalDistance(this.path);
        this.subscriptions.publish('graphic_changed', {
          recalcDisplayFields: [
            {
              label: 'Calculated Distance',
              value: this.totalDistance,
            },
          ],
        });
      });
    });
    this.polyline.addListener('click', (event) => this.handlePolylineClick(event, this.polyline));
    this.polyline.setMap(MapService.getMap());

    this.subscriptions.publish('open_info_window', {
      toolbar: this.getToolbarOptions(),
      featureType: 'polyline',
    });
  }

  /** @param {boolean} enabled */
  setPointerEvents(enabled) {
    this.svgOverlay.setPointerEvents(enabled);
  }

  /**
   * Adds the SVG representation to the map using a 2D array of lat/lng objects.
   */
  displaySvg() {
    // If there is no polyline, we are already in svg mode
    if (!this.polyline) return;

    // Update the graphic path
    this.path = getPathFromPolyline(this.polyline);
    // Display the svg overlay and update properties
    this.svgOverlay.setMap(MapService.getMap());
    this.svgOverlay.setOptions({path: this.path, strokeColor: this.color});
    // Hide the google maps polyline
    this.polyline.setMap(null);
  }

  /**
   * Adds a local event handler to the svg overlay
   * @param {string} eventType
   * @param {string} handlerName
   * @param {Function} handler
   */
  addLocalHandler(eventType, handlerName, handler) {
    // The SVGOverlayView does not care about the handlerName.
    this.svgOverlay.addListener(eventType, handler);
  }

  /**
   * Removes a local event handler from the svg overlay
   * @param {string} eventType
   * @param {string} handlerName
   * @param {Function} handler
   */
  removeLocalHandler(eventType, handlerName, handler) {
    // The SVGOverlayView does not care about the handlerName.
    this.svgOverlay.removeListener(eventType, handler);
  }

  /**
   * Sets clickable property of the polyline
   * @param {boolean} clickable
   */
  setClickable(clickable) {
    this._clickable = clickable;
  }

  /**
   * Getter for the 'clickable' value on the polyline
   * @return {boolean}
   */
  isClickable() {
    return this._clickable;
  }

  /**
   * Sets the map for the polyline in svg representation
   * @param {google.maps.Map|null} map
   */
  setMap(map) {
    if (map !== null) {
      this.svgOverlay.setMap(map);
    } else {
      this.svgOverlay.setMap(null);
    }
  }

  /**
   * Sets the highlight state of the svg overlay version of the polyline
   * @param {boolean} highlighted
   */
  setHighlight(highlighted) {
    this.svgOverlay.setHighlighted(highlighted);
  }

  /**
   * Sets the editing state of the polyline graphic
   * @param {boolean} editing
   * @return {boolean}
   */
  setEditing(editing) {
    // If the editing state is the same, do nothing
    if (this.editing === editing) return;
    // Update editing state and then swap the display
    this.editing = editing;
    if (editing) {
      this.displayPolyline();
    } else {
      this.displaySvg();
    }
    return false;
  }

  /**
   * Handles click events on the polyline for removing vertices
   * @param {Object} event
   * @param {google.maps.Polyline} polyline
   */
  handlePolylineClick(event, polyline) {
    if (event.domEvent.altKey) {
      // Remove vertex if alt key is held
      if ((event.vertex || event?.vertex === 0) && polyline.getPath().getLength() > 2) {
        polyline.getPath().removeAt(event.vertex);
      }
    }
  }

  /**
   * Subscribes the graphic to a given event
   * @param {string} eventName
   * @param {(event: SubscriptionEvent) => any} callback
   * @return {SubscriptionResult}
   */
  subscribe(eventName, callback) {
    return this.subscriptions.addSubscription(eventName, callback);
  }

  /**
   * Converts the graphic to a json representation for updating Firestore
   * @return {Object}
   */
  toJSON() {
    const superJSON = super.toJSON();
    const bounds = getBoundsFromPath(this.path).toJSON();
    return {
      ...superJSON,
      bounds,
      graphicData: {
        bounds,
        clickable: true,
        strokeColor: this.color,
        featureType: 'polyline',
        path: this.path,
      },
    };
  }

  /**
   * @typedef {Object} PolylinePicker
   * @property {string} title
   * @property {Function} getColor
   * @property {Function} setColor
   * @property {boolean} allowTransparent
   * @property {string[]} availableColors
   *
   * @typedef {Object} PolylineOption
   * @property {string} icon
   * @property {string} action
   * @property {string} name
   * @property {Array<PolylinePicker>} pickers
   *
   * @typedef {Object} WrappedOptions
   * @property {Array<Array<PolylineOption>>} toolbarOptions
   *
   * Gets the toolbar options
   * @return {WrappedOptions}
   */
  getToolbarOptions() {
    return {
      toolbarOptions: [
        [
          {
            icon: 'Fill',
            action: 'colorPicker',
            name: 'Change polyline color',
            pickers: [
              {
                title: 'Polyline Color',
                getColor: () => this.color,
                setColor: (color) => {
                  this.color = color;
                  this.polyline.setOptions({strokeColor: color});
                  this.subscriptions.publish('graphic_changed');
                },
                allowTransparent: false,
                availableColors: colors.overlayColorPalette,
              },
            ],
          },
        ],
      ],
    };
  }

  /**
   * @param {number} index
   */
  setZIndex(index) {
    this.svgOverlay.setZIndex(index);
  }

  /**
   * Gets the middle polyline index
   * @return {LatLngLiteral}
   */
  getLocation() {
    const middleIndex = Math.floor(this.path.length / 2);
    return {
      lat: this.path[middleIndex].lat,
      lng: this.path[middleIndex].lng,
    };
  }
}

/**
 * Creates a new PolylineGraphic object
 * @param {PolylineGraphicData} polylineData The options for the polyline.
 * @return {PolylineGraphic} The new PolylineGraphic object.
 */
function createPolylineGraphic(polylineData) {
  return new PolylineGraphic(polylineData);
}

export {
  PolylineGraphic,
  createPolylineGraphic,
  calculateTotalDistance,
};
